import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { STORAGE_KEY } from "@/components/explore-category-modal";
import {
  CATEGORIES,
  CATEGORY_ALL,
} from "@/components/game-categories/constants";
import { cn } from "@/lib/utils";
import { exploreCategory } from "@/store";

import useSelectedCategories from "../hooks/use-selected-categories";

export default function CategoryFilter() {
  const [gradientOpacity, setGradientOpacity] = useState(0);

  const { categoryUrl, primaryCategories } = useSelectedCategories();

  const handleCategory = (category: string) => () => {
    exploreCategory(category);
    localStorage.setItem(STORAGE_KEY, category);
  };

  useEffect(() => {
    const handleScroll = () => {
      setGradientOpacity(
        Math.min(
          Math.round(window.scrollY / (window.screen.height * 0.4 - 64) / 0.2) /
            10,
          1
        )
      );
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const activeCategories = useMemo(
    () => (primaryCategories.length ? primaryCategories : [CATEGORY_ALL]),
    [primaryCategories]
  );

  return (
    <ul
      className={cn(
        "sticky top-12.5 z-1 flex justify-between border-white/10 bg-light-black px-7 pt-3.5",
        "md:static md:justify-start md:border-b md:bg-transparent md:px-4 md:pt-0",
        'after:absolute after:top-0 after:left-0 after:block after:size-full after:w-12 after:bg-[url(/images/body.png)] after:bg-position-[center_-50px] after:bg-no-repeat after:opacity-(--gradient-opacity) after:shadow-0-4-8 after:shadow-midnight-velvet/40 after:content-[""] after:md:hidden',
        'before:absolute before:top-2.8 before:left-1/2 before:-mt-0.5 before:-ml-6 before:block before:h-1 before:w-12 before:rounded-[2px] before:bg-white/20 before:content-[""] before:md:hidden'
      )}
      style={
        {
          "--gradient-opacity": gradientOpacity,
        } as React.CSSProperties
      }
    >
      {Object.entries(CATEGORIES).map(([key, { Icon, label }]) => {
        const isSelected = activeCategories.includes(key);
        const color = isSelected
          ? "text-white"
          : "text-slate-gray md:text-pale-lavender";

        return (
          <li
            className={cn(
              "relative z-1 flex items-center gap-2 py-5 transition-opacity md:px-2 md:py-4.5",
              'after:absolute after:bottom-0 after:left-0 after:h-1 after:w-full after:rounded-[2px] after:bg-rose-red after:transition-opacity after:content-[""]',
              !isSelected && "after:opacity-0 md:opacity-50"
            )}
            key={key}
          >
            <Icon className={cn("size-4 transition-colors", color)} />
            <Link
              className={cn(
                "font-medium text-15/[1.2em] md:text-16/[1.3em]",
                color
              )}
              onClick={handleCategory(key)}
              {...categoryUrl(key)}
            >
              {label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
